import { defineQuery, defineQueryOptions } from '@pinia/colada'
import type { PaAPI } from '@rialtic/types'
import { STALE_TIME } from '@rialtic/utils'
import { activeOptions } from './activeOptions'

export const WORKSPACE_QUERY_KEYS = {
  root: ['paapi', 'workspace'] as const,
  byId: (id: string) => [...WORKSPACE_QUERY_KEYS.root, id] as const,
  byIdWithConnectors: (id: string) =>
    [...WORKSPACE_QUERY_KEYS.byId(id), 'with-connectors'] as const,
  clientReviewStatusByWorkspaceId: (id: string) =>
    [...WORKSPACE_QUERY_KEYS.byId(id), 'client-review-status'] as const,
}
export const workspaceQuery = defineQueryOptions({
  key: WORKSPACE_QUERY_KEYS.root,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/workspace']>('/api/paapi/workspace')
  },
  staleTime: STALE_TIME.FIVE_MINUTES,
})
export const useWorkspaces = defineQuery(workspaceQuery)

export const activeWorkspacesQuery = defineQueryOptions({
  ...workspaceQuery,
  key: [...WORKSPACE_QUERY_KEYS.root, 'active'] as const,
  query: async (context) => activeOptions(await workspaceQuery.query(context)),
})
export const useActiveWorkspaces = defineQuery(activeWorkspacesQuery)

export const workspaceByIdWithConnectors = defineQueryOptions((id: string) => {
  return {
    key: WORKSPACE_QUERY_KEYS.byIdWithConnectors(id),
    enabled: Boolean(id),
    query: async () => {
      const client = useRequestFetch()
      const workspaceData = await client<PaAPI.Get['/workspace/{id}']>(
        `/api/paapi/workspace/${id}` as const,
        {
          query: { 'include-connectors': true },
        },
      ).catch(async (error: unknown) => {
        /* Some admins get a 403 here but can still list workspaces, and the
           list entry carries the name the overrides pages show. */
        const listed = (
          await client<PaAPI.Get['/workspace']>('/api/paapi/workspace')
        ).find((workspace) => workspace.id === id)
        if (!listed) throw error
        return listed as unknown as PaAPI.Get['/workspace/{id}']
      })

      const queryCache = useQueryCache()

      // Intentionally pre-populate the client review status cache to avoid
      // additional network requests when the client review status query is used.
      // This optimizes performance by sharing the workspace data that already
      // includes the client_review_status field.
      if (workspaceData.client_review_status !== undefined)
        queryCache.setQueryData(
          WORKSPACE_QUERY_KEYS.clientReviewStatusByWorkspaceId(id),
          workspaceData.client_review_status,
        )

      return workspaceData
    },
    staleTime: STALE_TIME.FIVE_MINUTES,
  }
})

export const clientReviewStatusByWorkspaceId = defineQueryOptions(
  (id: string) => {
    return {
      key: WORKSPACE_QUERY_KEYS.clientReviewStatusByWorkspaceId(id),
      enabled: Boolean(id),
      query: () => {
        const client = useRequestFetch()
        return client<PaAPI.Get['/client-review/status/{workspace}']>(
          `/api/paapi/client-review/status/${id}` as const,
          {
            headers: {
              'x-rialtic-workspace-id': id,
            },
          },
        )
      },
      staleTime: STALE_TIME.FIVE_MINUTES,
    }
  },
)
